import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const BASE_URL = "https://adv3nture.io";
const OG_IMAGE =
  "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/0ebb8143-0e00-42ea-893b-2371fe46fa7b/id-preview-42714d67--94944aef-c355-41d5-913a-6feeb9c05bdd.lovable.app-1773403436973.png";

interface SEOHeadProps {
  title?: string;
  description?: string;
  jsonLd?: Record<string, unknown>;
}

const defaultMeta: Record<string, { title: string; description: string }> = {
  "/": {
    title: "AdV3nture | Startup Fundraising & Angel Investing Platform",
    description:
      "Connect with angel investors, VCs, and startup founders. AdV3nture is a fundraising discovery platform matching startups with investors by stage, sector, and thesis. Seed funding, pre-seed rounds, and venture capital, all in one place.",
  },
  "/about": {
    title: "About AdV3nture | Startup Fundraising & Venture Capital Platform",
    description:
      "AdV3nture is a UK fintech platform for startup fundraising and angel investing. Connecting early-stage founders with seed investors, venture capitalists, and angel networks.",
  },
  "/how-it-works": {
    title: "How It Works | Find Investors & Fundraise | AdV3nture",
    description:
      "Discover how AdV3nture connects startup founders with angel investors and VCs. Four steps: discover, evaluate, connect, and execute your fundraising round.",
  },
  "/for-founders": {
    title: "For Founders | Find Angel Investors & VCs | AdV3nture",
    description:
      "Raise your seed round, pre-seed, or Series A with AdV3nture. Get discovered by angel investors and venture capitalists aligned with your stage, sector, and vision.",
  },
  "/for-investors": {
    title: "For Investors | Startup Deal Flow & Angel Investing | AdV3nture",
    description:
      "Discover startup investment opportunities. Angel investing, seed funding, and venture capital deal flow filtered by stage, sector, geography, and thesis alignment.",
  },
  "/security": {
    title: "Security & Compliance | AdV3nture Fundraising Platform",
    description:
      "Enterprise-grade security for startup fundraising. KYC verification, encrypted communications, and privacy-by-design protecting founders and investors.",
  },
  "/contact": {
    title: "Contact AdV3nture | Startup Fundraising Support",
    description:
      "Get in touch with AdV3nture. Whether you're a startup founder seeking angel investors or a VC looking for deal flow, our team is here to help.",
  },
  "/fundraising-statistics": {
    title: "Startup Fundraising Statistics 2025 | Seed, Pre-Seed & Angel Rounds",
    description:
      "Comprehensive startup fundraising data: pre-seed and seed round sizes, angel investing trends, valuations, equity dilution, survival rates, and venture capital market insights.",
  },
  "/privacy": {
    title: "Privacy Policy | AdV3nture Fundraising Platform",
    description:
      "AdV3nture privacy policy. How we protect founder and investor data on our startup fundraising and angel investing platform.",
  },
  "/terms": {
    title: "Terms & Conditions | AdV3nture",
    description:
      "Terms governing use of AdV3nture, the startup fundraising and investor discovery platform for founders, angel investors, and VCs.",
  },
  "/cookies": {
    title: "Cookie Policy | AdV3nture",
    description:
      "AdV3nture cookie policy. Understand what cookies we use on our startup fundraising platform and how to manage preferences.",
  },
};

const organizationJsonLd = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "AdV3nture",
  url: BASE_URL,
  logo: `${BASE_URL}/favicon.ico`,
  description:
    "Structured discovery platform connecting founders with aligned investors through thesis-based matching.",
  sameAs: ["https://www.linkedin.com/company/adv3ntureio/"],
  foundingDate: "2024",
  address: {
    "@type": "PostalAddress",
    addressCountry: "GB",
  },
};

const websiteJsonLd = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "AdV3nture",
  url: BASE_URL,
  description:
    "Structured discovery platform connecting founders with aligned investors.",
};

const softwareJsonLd = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "AdV3nture",
  applicationCategory: "FinanceApplication",
  operatingSystem: "Web",
  url: BASE_URL,
  description:
    "Platform connecting startup founders with early-stage investors through structured discovery, thesis-aligned matching, and secure interactions.",
  offers: {
    "@type": "Offer",
    price: "0",
    priceCurrency: "GBP",
    availability: "https://schema.org/InStock",
    description: "Early access — free to join",
  },
};

const SEOHead = ({ title, description, jsonLd }: SEOHeadProps) => {
  const { pathname } = useLocation();

  useEffect(() => {
    const meta = defaultMeta[pathname];
    const pageTitle = title || meta?.title || "AdV3nture";
    const pageDescription =
      description ||
      meta?.description ||
      "Structured discovery platform connecting founders with aligned investors.";

    // Title
    document.title = pageTitle;

    // Meta description
    const setMeta = (attr: string, key: string, content: string) => {
      const selector = `meta[${attr}="${key}"]`;
      const tag =
        document.querySelector(selector) || document.createElement("meta");
      tag.setAttribute(attr, key);
      tag.setAttribute("content", content);
      if (!tag.parentNode) document.head.appendChild(tag);
    };

    setMeta("name", "description", pageDescription);

    // OG tags
    setMeta("property", "og:title", pageTitle);
    setMeta("property", "og:description", pageDescription);
    setMeta("property", "og:url", `${BASE_URL}${pathname}`);
    setMeta("property", "og:type", "website");
    setMeta("property", "og:image", OG_IMAGE);
    setMeta("property", "og:site_name", "AdV3nture");

    // Twitter tags
    setMeta("name", "twitter:title", pageTitle);
    setMeta("name", "twitter:description", pageDescription);
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:image", OG_IMAGE);

    // Canonical
    let canonical = document.querySelector(
      'link[rel="canonical"]'
    ) as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", `${BASE_URL}${pathname}`);

    // JSON-LD
    const existingLd = document.getElementById("seo-jsonld");
    if (existingLd) existingLd.remove();

    const schemas: Record<string, unknown>[] = [
      organizationJsonLd,
      websiteJsonLd,
    ];
    // Add SoftwareApplication schema only on homepage
    if (pathname === "/") schemas.push(softwareJsonLd);
    if (jsonLd) schemas.push(jsonLd);

    const script = document.createElement("script");
    script.id = "seo-jsonld";
    script.type = "application/ld+json";
    script.textContent = JSON.stringify(schemas);
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById("seo-jsonld");
      if (el) el.remove();
    };
  }, [pathname, title, description, jsonLd]);

  return null;
};

export default SEOHead;
